<template>
  <div>
    <h2 class="heading-2">{{ i18n(translations.title) }}</h2>

    <div class="mt-32">
      <div class="grid gap-32 max-md:hidden md:grid-cols-2 lg:grid-cols-3">
        <RevButtonCard
          v-for="category in categories"
          :key="category.id"
          class="bg-float-default-low flex h-[126px] items-center gap-12 px-24 py-32"
          :data-qa="getCategoryDataQA(category.id.toString())"
          :data-test="getCategoryDataQA(category.id.toString())"
          :to="category.url"
        >
          <div
            class="bg-static-default-mid rounded-full flex size-[62px] shrink-0 items-center justify-center p-14"
          >
            <component
              :is="FAQ_CATEGORIES_ICONS[category.id] || IconInfo"
              :label="category.name"
            />
          </div>

          <h3 class="heading-3">
            {{ category.name }}
          </h3>
        </RevButtonCard>
      </div>

      <RevList
        class="bg-action-default-low shadow-short rounded-md md:hidden"
        :hasExternalBorders="false"
        role="listbox"
      >
        <RevListItemAction
          v-for="category in categories"
          :key="category.id"
          class="items-center"
          :data-qa="getCategoryDataQA(category.id.toString())"
          :data-test="getCategoryDataQA(category.id.toString())"
          :to="category.url"
        >
          <template #prefix>
            <div class="flex size-[24px] items-center justify-center">
              <component
                :is="FAQ_CATEGORIES_ICONS[+category.id] || IconInfo"
                :label="category.name"
              />
            </div>
          </template>
          <template #label>
            <span class="body-1">
              {{ category.name }}
            </span>
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemAction>
      </RevList>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { type MaybeRef, computed, unref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonCard } from '@ds/components/ButtonCard'
import { RevList } from '@ds/components/List'
import { RevListItemAction } from '@ds/components/ListItemAction'
import { IconAvatar } from '@ds/icons/IconAvatar'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconContact } from '@ds/icons/IconContact'
import { IconCycle } from '@ds/icons/IconCycle'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconMoney } from '@ds/icons/IconMoney'
import { IconQuality } from '@ds/icons/IconQuality'
import { IconRefund } from '@ds/icons/IconRefund'
import { IconShoppingBag } from '@ds/icons/IconShoppingBag'
import { IconSim } from '@ds/icons/IconSim'
import { IconSiren } from '@ds/icons/IconSiren'
import { IconSwap } from '@ds/icons/IconSwap'
import { IconTools } from '@ds/icons/IconTools'
import { IconTruck } from '@ds/icons/IconTruck'

import type { FAQCategory } from '~/api/help-center/types/FaqCategories.types'
import { isConfigDefined } from '~/scopes/care-commons/utils/isConfigDefined'

import translations from './HelpCenterFAQCategories.translations'

const props = defineProps<{
  categories: MaybeRef<FAQCategory[] | null>
}>()

const i18n = useI18n()

const categories = computed(() => Array.from(unref(props.categories) || []))

const config = useRuntimeConfig()

const FAQ_CATEGORIES_ICONS = {
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_ACCOUNT_ID,
    'ZENDESK_CATEGORY_ACCOUNT_ID',
  )]: IconAvatar,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_BUYING_ID,
    'ZENDESK_CATEGORY_BUYING_ID',
  )]: IconShoppingBag,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_CONTACT_AND_SUPPORT_ID,
    'ZENDESK_CATEGORY_CONTACT_AND_SUPPORT_ID',
  )]: IconContact,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_MOBILE_PLAN_ID,
    'ZENDESK_CATEGORY_MOBILE_PLAN_ID',
  )]: IconSim,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_PAYMENTS_ID,
    'ZENDESK_CATEGORY_PAYMENTS_ID',
  )]: IconMoney,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_PROBLEM_WITH_MY_ORDER_ID,
    'ZENDESK_CATEGORY_PROBLEM_WITH_MY_ORDER_ID',
  )]: IconSiren,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_RETURNS_ID,
    'ZENDESK_CATEGORY_RETURNS_ID',
  )]: IconRefund,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_SELLS_ID,
    'ZENDESK_CATEGORY_SELLS_ID',
  )]: IconSwap,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_SHIPPING_ID,
    'ZENDESK_CATEGORY_SHIPPING_ID',
  )]: IconTruck,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_WARRANTY_ID,
    'ZENDESK_CATEGORY_WARRANTY_ID',
  )]: IconQuality,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_TROUBLESHOOTING_ID,
    'ZENDESK_CATEGORY_TROUBLESHOOTING_ID',
  )]: IconTools,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_RECYCLE_ID,
    'ZENDESK_CATEGORY_RECYCLE_ID',
  )]: IconCycle,
} as const

const FAQ_CATEGORIES_DATA_ID = {
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_ACCOUNT_ID,
    'ZENDESK_CATEGORY_ACCOUNT_ID',
  )]: 'faq-category-account',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_BUYING_ID,
    'ZENDESK_CATEGORY_BUYING_ID',
  )]: 'faq-category-buying',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_MOBILE_PLAN_ID,
    'ZENDESK_CATEGORY_MOBILE_PLAN_ID',
  )]: 'faq-category-mobile-plan',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_PAYMENTS_ID,
    'ZENDESK_CATEGORY_PAYMENTS_ID',
  )]: 'faq-category-payments',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_RETURNS_ID,
    'ZENDESK_CATEGORY_RETURNS_ID',
  )]: 'faq-category-returns',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_SELLS_ID,
    'ZENDESK_CATEGORY_SELLS_ID',
  )]: 'faq-category-sells',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_SHIPPING_ID,
    'ZENDESK_CATEGORY_SHIPPING_ID',
  )]: 'faq-category-shipping',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_WARRANTY_ID,
    'ZENDESK_CATEGORY_WARRANTY_ID',
  )]: 'faq-category-warranty',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_RECYCLE_ID,
    'ZENDESK_CATEGORY_RECYCLE_ID',
  )]: 'faq-category-recycle',
} as const

function getCategoryDataQA(categoryId: string) {
  return (
    FAQ_CATEGORIES_DATA_ID[categoryId] ||
    `faq-category-${categoryId.toString()}`
  )
}
</script>
